// src/sync/SyncControl.jsx
// In the header: how this device stands with the server, and how to switch offline mode on. Also the conflict
// dialog, shown whenever the two sides changed independently.
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '../components/Toast';
import { useNavigate } from 'react-router-dom';
import { endGuestSession } from '../guest';
import { useSync } from './SyncProvider';

const LABELS = {
    idle: 'Saved on this device and the server',
    syncing: 'Syncing…',
    pending: 'Saved on this device, not on the server yet',
    conflict: 'Changed in two places',
    error: 'Could not sync',
};
const SHORT = { idle: 'Synced', syncing: 'Syncing…', pending: 'Not synced yet', conflict: 'Conflict', error: 'Sync problem' };

// Drawn on the page itself, not inside the header: the header's blur makes it the frame of anything "fixed" in it,
// which cut the dialog down to the header's size.
function ConflictDialog({ onChoose, busy }) {
    return createPortal(
        <div className="modal-backdrop">
            <div className="modal" role="dialog" aria-modal="true" aria-labelledby="conflict-title">
                <h2 id="conflict-title">Changed on this device and on the server</h2>
                <p>
                    Your tournaments were changed here while offline, and also somewhere else. Which version should be kept? The
                    other one is saved as a backup first (see Backups), so nothing is lost.
                </p>
                <div className="modal-actions">
                    <button className="btn" disabled={busy} onClick={() => onChoose('server')}>
                        Keep the server’s version
                    </button>
                    <button className="btn btn-primary" disabled={busy} onClick={() => onChoose('device')}>
                        Keep this device’s version
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

// One question per item that both sides changed differently, grouped by tournament.
function MergeDialog({ questions, onAnswer, onWhole, busy }) {
    const [choices, setChoices] = useState({});
    const answered = questions.every((question) => choices[question.key]);
    const setAll = (side) => setChoices(Object.fromEntries(questions.map((question) => [question.key, side])));
    const groups = questions.reduce((all, question) => {
        (all[question.tournament] = all[question.tournament] || []).push(question);
        return all;
    }, {});
    return createPortal(
        <div className="modal-backdrop">
            <div className="modal merge-dialog" role="dialog" aria-modal="true" aria-labelledby="merge-title">
                <h2 id="merge-title">Both versions changed the same things</h2>
                <p>Everything else was merged already. For these, choose which value to keep.</p>
                <div className="merge-all">
                    <button className="btn btn-sm" onClick={() => setAll('mine')}>
                        All from this device
                    </button>
                    <button className="btn btn-sm" onClick={() => setAll('theirs')}>
                        All from the server
                    </button>
                </div>
                {Object.entries(groups).map(([tournament, items]) => (
                    <fieldset key={tournament} className="merge-group">
                        <legend>{tournament}</legend>
                        {items.map((question) => (
                            <div key={question.key} className="merge-item" role="group" aria-label={question.label}>
                                <div className="merge-label">{question.label}</div>
                                {['mine', 'theirs'].map((side) => (
                                    <label key={side} className={`merge-choice${choices[question.key] === side ? ' chosen' : ''}`}>
                                        <input
                                            type="radio"
                                            name={question.key}
                                            checked={choices[question.key] === side}
                                            onChange={() => setChoices({ ...choices, [question.key]: side })}
                                        />
                                        <span>{side === 'mine' ? 'This device' : 'Server'}</span>
                                        <strong>{question[side]}</strong>
                                    </label>
                                ))}
                            </div>
                        ))}
                    </fieldset>
                ))}
                <div className="modal-actions">
                    <button className="btn btn-primary" disabled={!answered || busy} onClick={() => onAnswer(choices)}>
                        Merge
                    </button>
                </div>
                <p className="hint">
                    Or keep{' '}
                    <button className="link-button" onClick={() => onWhole('device')}>
                        this device’s whole version
                    </button>{' '}
                    or{' '}
                    <button className="link-button" onClick={() => onWhole('server')}>
                        the server’s whole version
                    </button>
                    . The other one is saved as a backup.
                </p>
            </div>
        </div>,
        document.body
    );
}

function SyncControl() {
    const sync = useSync();
    const toast = useToast();
    const navigate = useNavigate();
    const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine !== false);

    useEffect(() => {
        const update = () => setOnline(navigator.onLine !== false);
        window.addEventListener('online', update);
        window.addEventListener('offline', update);
        return () => {
            window.removeEventListener('online', update);
            window.removeEventListener('offline', update);
        };
    }, []);

    if (sync.isGuest) {
        // to the login page, with all the ways to sign in; what the guest made waits on the device
        const signIn = () => {
            endGuestSession();
            navigate('/');
        };
        return (
            <>
                <span className="sync-status sync-pending" title="Your tournaments are only on this device">
                    Guest
                </span>
                <button className="btn btn-primary btn-sm" onClick={signIn}>
                    Sign in
                </button>
            </>
        );
    }

    if (!sync.signedIn && !sync.enabled) return null;

    const choose = async (choice) => {
        const result = await sync.resolve(choice);
        if (result && (result.outcome === 'pulled' || result.outcome === 'pushed')) {
            toast.success(choice === 'server' ? 'Now showing the server’s version.' : 'This device’s version is now on the server.');
            setTimeout(() => window.location.reload(), 800); // what is on screen is from before
        }
    };

    if (!sync.enabled) return null; // (offline mode switches itself on after signing in: nothing to ask for)

    const answer = async (choices) => {
        const result = await sync.answer(choices);
        if (result && result.outcome === 'merged') toast.success('Both versions are merged.');
    };

    const phase = sync.phase;
    return (
        <>
            {/* only shown when something needs attention; otherwise it is there for screen readers (and checks) only */}
            <span
                className={`sync-status sync-${phase}${phase === 'error' ? '' : ' visually-hidden'}`}
                role="status"
                title={LABELS[phase]}
            >
                {!online && phase !== 'conflict' ? 'Offline' : SHORT[phase]}
            </span>
            {phase === 'conflict' && sync.questions && sync.questions.length > 0 && (
                <MergeDialog questions={sync.questions} onAnswer={answer} onWhole={choose} busy={false} />
            )}
            {phase === 'conflict' && !(sync.questions && sync.questions.length > 0) && <ConflictDialog onChoose={choose} busy={false} />}
        </>
    );
}

export default SyncControl;
